import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [secretariaDesbloqueada, setSecretariaDesbloqueada] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedSecretaria = localStorage.getItem('secretaria_desbloqueada');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    if (savedSecretaria) {
      setSecretariaDesbloqueada(JSON.parse(savedSecretaria));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      
      // Limpar secretaria desbloqueada ao fazer novo login
      localStorage.removeItem('secretaria_desbloqueada');
      setSecretariaDesbloqueada(null);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Erro ao fazer login'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('secretaria_desbloqueada');
    setUser(null);
    setSecretariaDesbloqueada(null);
    window.location.href = '/login';
  };

  const isAdmin = () => {
    return user?.profile === 'admin';
  };

  const isSupervisor = () => {
    return user?.profile === 'supervisor';
  };

  const isUser = () => {
    return user?.profile === 'usuario';
  };

  // NOVO: Perfil secretaria
  const isSecretaria = () => {
    return user?.profile === 'secretaria';
  };

  const canManageUsers = () => {
    return isAdmin();
  };

  // APENAS Admin pode editar cadastros (Supervisor só visualiza)
  const canManageCadastros = () => {
    return isAdmin();
  };

  const canManageAbastecimentos = () => {
    return isAdmin();
  };

  // Supervisor e Usuário só podem visualizar
  const canOnlyView = () => {
    return isSupervisor() || isUser();
  };

  // NOVO: Desbloquear secretaria
  const desbloquearSecretaria = (secretariaId, secretariaNome) => {
    const data = { id: secretariaId, nome: secretariaNome };
    localStorage.setItem('secretaria_desbloqueada', JSON.stringify(data));
    setSecretariaDesbloqueada(data);
  };

  // NOVO: Verificar se secretaria está desbloqueada
  const isSecretariaDesbloqueada = (secretariaId) => {
    return secretariaDesbloqueada?.id === secretariaId;
  };

  // NOVO: Pode gerenciar na secretaria (se for perfil secretaria e tiver desbloqueado)
  const canManageInSecretaria = (secretariaId) => {
    if (isAdmin()) return true;
    if (isSecretaria() && isSecretariaDesbloqueada(secretariaId)) return true;
    return false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      isAdmin, 
      isSupervisor, 
      isUser,
      isSecretaria,
      canManageUsers,
      canManageCadastros,
      canManageAbastecimentos,
      canOnlyView,
      desbloquearSecretaria,
      isSecretariaDesbloqueada,
      canManageInSecretaria,
      secretariaDesbloqueada
    }}>
      {children}
    </AuthContext.Provider>
  );
};
